import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { api } from '../api/client'

const STORAGE_KEY = 'kamakhya-cart'
const lineKey = (item) => `${item.id}::${item.size || ''}::${Number(item.unitsPerPack || 0)}`

const readStoredCart = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const CartContext = createContext(null)

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readStoredCart)
  const [submittedOrder, setSubmittedOrder] = useState(null)
  const [orderSubmitting, setOrderSubmitting] = useState(false)
  const [orderSubmitError, setOrderSubmitError] = useState('')
  const [toast, setToast] = useState(false)
  const toastTimer = useRef(null)

  // Keep localStorage in sync so the cart survives page reloads.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch {
      // Ignore storage errors (e.g. private browsing mode).
    }
  }, [items])

  // Auto-hide the toast after a short delay.
  useEffect(() => () => clearTimeout(toastTimer.current), [])

  const showToast = useCallback(() => {
    setToast(true)
    clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(false), 2000)
  }, [])

  const addToCart = useCallback((product, options = {}) => {
    if (!product || !product.id) return

    const { packSize, unitsPerPack, batches = 1, price } = options
    const quantity = Math.max(1, Number(batches) || 1)
    const unitCost = Number.isFinite(Number(price)) ? Number(price) : 0
    const linePrice = Math.round(unitCost * (Number(unitsPerPack) || 1) * quantity * 100) / 100
    const productName = product.title || product.name || 'Kamakhya Product'
    const productImage = product.image || product.images?.[0] || ''

    setItems((prev) => {
      const existing = prev.find(
        (item) =>
          lineKey(item) === lineKey({ id: product.id, size: packSize, unitsPerPack })
      )

      if (existing) {
        return prev.map((item) =>
          item === existing
            ? {
                ...item,
                sku: product.sku || item.sku || '',
                shippingCost: Number(product.shippingCost) || 0,
                quantity: item.quantity + quantity,
                price: Math.round((Number(item.price || 0) + linePrice) * 100) / 100,
              }
            : item
        )
      }

      return [
        ...prev,
        {
          id: product.id,
          name: productName,
          sku: product.sku || '',
          image: productImage,
          size: packSize || '',
          unitsPerPack: Number(unitsPerPack || 0),
          quantity,
          price: linePrice,
          unitCost: Math.round(unitCost * 100) / 100,
          shippingCost: Number(product.shippingCost) || 0,
        },
      ]
    })

    showToast()
  }, [showToast])

  const updateQuantity = (key, delta) => {
    setItems((prev) =>
      prev.map((item) => {
        if (lineKey(item) !== key) return item
        const nextQuantity = Math.max(1, item.quantity + delta)
        const unitCost = Number.isFinite(Number(item.unitCost))
          ? Number(item.unitCost)
          : Number(item.price || 0) / (Math.max(1, Number(item.quantity) || 1) * (Number(item.unitsPerPack) || 1))
        return {
          ...item,
          quantity: nextQuantity,
          unitCost,
          price: Math.round(unitCost * (item.unitsPerPack || 1) * nextQuantity * 100) / 100,
        }
      })
    )
  }

  const removeFromCart = (key) => {
    setItems((prev) => prev.filter((item) => lineKey(item) !== key))
  }

  const clearCart = () => setItems([])

  const cartCount = items.reduce((sum, item) => sum + item.quantity, 0)
  const subtotal = items.reduce((sum, item) => sum + item.price, 0)
  const shipping = items.reduce((sum, item) => sum + (Number(item.shippingCost) || 0) * item.quantity, 0)
  const tax = Math.round(subtotal * 0.13 * 100) / 100
  const total = Math.round((subtotal + tax + shipping) * 100) / 100

  // Without this actually reaching the backend, every "order" placed
  // through the storefront would exist only in this browser's memory —
  // gone on refresh, and never seen by anyone at the business. The
  // reference number now comes from the real created record, not a
  // random client-side string that corresponds to nothing.
  const submitOrder = useCallback(async (details = {}) => {
    setOrderSubmitting(true)
    setOrderSubmitError('')
    try {
      const result = await api.createSubmission({
        type: 'order',
        name: details.fullName || details.companyName,
        email: details.email,
        phone: details.phone,
        payload: {
          items,
          subtotal,
          tax,
          shipping,
          total,
          companyName: details.companyName || '',
          deliveryAddress: details.deliveryAddress || '',
          city: details.city || '',
          country: details.country || '',
          orderNotes: details.orderNotes || '',
        },
      })

      setSubmittedOrder({
        items,
        subtotal,
        tax,
        shipping,
        total,
        ref: result.submission._id,
        companyName: details.companyName || '',
        fullName: details.fullName || '',
        email: details.email || '',
        phone: details.phone || '',
        deliveryAddress: details.deliveryAddress || '',
        city: details.city || '',
        country: details.country || '',
        orderNotes: details.orderNotes || '',
        submittedAt: result.submission.createdAt,
      })
      setItems([])
      return { ok: true }
    } catch (err) {
      setOrderSubmitError(err.message || 'Something went wrong — please try again.')
      return { ok: false, message: err.message }
    } finally {
      setOrderSubmitting(false)
    }
  }, [items, subtotal, shipping, tax, total])

  const value = useMemo(
    () => ({
      items,
      cartCount,
      subtotal,
      shipping,
      tax,
      total,
      submittedOrder,
      orderSubmitting,
      orderSubmitError,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      submitOrder,
    }),
    [items, cartCount, subtotal, shipping, tax, total, submittedOrder, orderSubmitting, orderSubmitError, addToCart, submitOrder]
  )

  return (
    <CartContext.Provider value={value}>
      {children}

      {/* "Product added to cart" popup — bottom right corner */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: '92px',
            right: '24px',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: '#2E3192',
            color: '#ffffff',
            fontFamily: "'Poppins', 'Segoe UI', sans-serif",
            fontSize: '14px',
            fontWeight: 500,
            padding: '12px 20px',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(46, 49, 146, 0.3)',
            animation: 'kamakhya-toast-in 0.25s ease-out',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="#E38F2E"
            stroke="none"
            aria-hidden="true"
          >
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0" />
          </svg>
          Product added to cart
        </div>
      )}

      <style>{`
        @keyframes kamakhya-toast-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
